/**
 * Conferência de voz e vídeo da mesa (ConferenceController / sessão de mídia do
 * original): entrar, microfone, câmera, sair e os quadros de quem está na
 * chamada. Fica no painel Participantes ("Voz e vídeo"); no celular, numa folha.
 * A mídia vai direto entre os navegadores (malha P2P); o servidor só troca a
 * sinalização.
 */
import { useEffect, useRef } from 'react';
import { useT } from '../../i18n/index.js';
import { RTC_LIMITS } from '../../shared.js';
import { useApp } from '../../store.js';
import { Icon } from '../../ui/icons.js';
import type { VoiceChat } from '../../voice/useVoiceChat.js';
import './voice.css';

/** `inline`: no painel Participantes, parte da página; sem ele (a folha do celular), um diálogo. */
export function ConferencePanel({ voice, inline = false }: { voice: VoiceChat; inline?: boolean }) {
  const t = useT();
  const me = useApp((s) => s.user);
  const users = useApp((s) => s.users);
  const members = useApp((s) => s.members);
  const peerMedia = useApp((s) => s.peerMedia);
  const others = users.filter((u) => u.id !== me?.id);
  // quem está publicando áudio ou vídeo está na chamada
  const inCallOthers = others.filter((u) => voice.remoteStreams[u.id] || peerMedia[u.id]?.audio || peerMedia[u.id]?.video);
  const name = (id: string, fallback: string) => members[id]?.nick ?? fallback;

  return (
    <section className="rd-conf" role={inline ? undefined : 'dialog'} aria-label={t('room.voice')}>
      <header className="rd-conf__head">
        <h3>{t('room.voice')}</h3>
        <span className={`rd-conf__state${voice.inCall ? ' is-on' : ''}`}>
          {voice.inCall ? t('voice.inCallCount', { n: voice.peerCount + 1 }) : t('voice.outOfCall')}
        </span>
      </header>

      {voice.error ? (
        <p className="rd-conf__error" role="alert">
          {t(`voice.error.${voice.error}`)}
        </p>
      ) : null}

      {voice.inCall ? (
        <div className="rd-conf__controls">
          <button
            type="button"
            className={`rd-roundBtn${voice.micOn ? '' : ' rd-conf__off'}`}
            aria-pressed={voice.micOn}
            title={voice.micOn ? t('voice.micOff') : t('voice.micOn')}
            aria-label={voice.micOn ? t('voice.micOff') : t('voice.micOn')}
            onClick={voice.toggleMic}
          >
            <Icon name={voice.micOn ? 'mic' : 'micOff'} size={18} />
          </button>
          <button
            type="button"
            className={`rd-roundBtn${voice.camOn ? '' : ' rd-conf__off'}`}
            aria-pressed={voice.camOn}
            title={voice.camOn ? t('voice.camOff') : t('voice.camOn')}
            aria-label={voice.camOn ? t('voice.camOff') : t('voice.camOn')}
            onClick={() => void voice.toggleCam()}
          >
            <Icon name={voice.camOn ? 'camera' : 'cameraOff'} size={18} />
          </button>
          <button type="button" className="rd-roundBtn rd-conf__leave" title={t('voice.leave')} aria-label={t('voice.leave')} onClick={voice.leave}>
            <Icon name="hangup" size={20} />
          </button>
        </div>
      ) : (
        <div className="rd-conf__join">
          <p>{t('voice.intro')}</p>
          <button type="button" className="rd-btn rd-btn--action" disabled={voice.joining} onClick={() => void voice.join()}>
            <Icon name="mic" size={16} />
            {voice.joining ? t('voice.asking') : t('voice.join')}
          </button>
        </div>
      )}

      {others.length > RTC_LIMITS.maxMeshPeers ? <p className="rd-conf__note">{t('voice.bigRoom', { n: others.length })}</p> : null}

      {voice.inCall ? (
        <div className="rd-conf__tiles">
          <Tile stream={voice.localStream} label={t('voice.you', { name: me ? name(me.id, me.displayName) : '' })} micOn={voice.micOn} video={voice.camOn} />
          {inCallOthers.map((u) => (
            <Tile
              key={u.id}
              stream={voice.remoteStreams[u.id] ?? null}
              label={name(u.id, u.displayName)}
              micOn={!!peerMedia[u.id]?.audio}
              video={!!peerMedia[u.id]?.video}
            />
          ))}
        </div>
      ) : null}
      {voice.inCall && inCallOthers.length === 0 ? <p className="rd-conf__note">{t('voice.alone')}</p> : null}
    </section>
  );
}

/**
 * Saída de som da conferência: um <audio> por pessoa, sempre montado na mesa
 * (o painel pode estar fechado e a conversa continua).
 */
export function ConferenceAudio({ voice }: { voice: VoiceChat }) {
  return (
    <div hidden>
      {Object.entries(voice.remoteStreams).map(([id, stream]) => (
        <RemoteAudio key={id} stream={stream} />
      ))}
    </div>
  );
}

function RemoteAudio({ stream }: { stream: MediaStream }) {
  const ref = useRef<HTMLAudioElement>(null);
  const volume = useApp((s) => (s.settings.audioMuted ? 0 : 1));
  // a saída escolhida em Configurações → Áudio e vídeo (vazio: a do sistema)
  const speaker = useApp((s) => s.settings.speakerDeviceId);
  useEffect(() => {
    const el = ref.current;
    if (el && el.srcObject !== stream) el.srcObject = stream;
  }, [stream]);
  useEffect(() => {
    if (ref.current) ref.current.volume = volume;
  }, [volume]);
  useEffect(() => {
    const el = ref.current as (HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }) | null;
    // sem `setSinkId` (Safari), ou o aparelho sumiu: fica na saída de antes
    el?.setSinkId?.(speaker).catch(() => undefined);
  }, [speaker]);
  return <audio ref={ref} autoPlay />;
}

/** Quadro de uma pessoa: vídeo (sempre mudo; o som sai do ConferenceAudio) ou a inicial. */
function Tile({ stream, label, micOn, video }: { stream: MediaStream | null; label: string; micOn: boolean; video: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (el && el.srcObject !== stream) el.srcObject = stream;
  }, [stream]);
  return (
    <figure className={`rd-conf__tile${video ? ' has-video' : ''}`}>
      <video ref={ref} autoPlay playsInline muted />
      {!video ? <span className="rd-conf__avatar">{label.trim().slice(0, 1).toUpperCase()}</span> : null}
      <figcaption>
        <Icon name={micOn ? 'mic' : 'micOff'} size={12} />
        <span>{label}</span>
      </figcaption>
    </figure>
  );
}
